import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useNavigationType, useSearchParams } from 'react-router-dom'

/**
 * ÉTAT DES LISTES DANS L'URL (#259).
 *
 * ══════════════════════════════════════════════════════════════════════════
 * LE DÉFAUT RETIRÉ
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Recherche, filtres et onglets des listes vivaient dans des `useState` : un
 * agent qui filtrait Contacts, ouvrait une fiche puis revenait retrouvait la
 * liste à neuf. Ce module met l'état de liste DANS L'URL, sur le motif déjà
 * éprouvé de `pages/messaging/channel.ts` :
 *
 *   · `replace: true` — taper une recherche ou changer un filtre n'empile
 *     aucune entrée d'historique : « précédent » quitte la liste, il ne rejoue
 *     pas chaque frappe ;
 *   · les valeurs PAR DÉFAUT sont omises de l'URL (`/contacts` reste l'adresse
 *     canonique de la liste non filtrée) ;
 *   · le parsing est TOLÉRANT : une valeur inconnue ou malformée (lien ancien,
 *     saisie à la main) retombe sur le défaut, jamais sur une erreur ;
 *   · les paramètres qui n'appartiennent pas au schéma sont conservés.
 *
 * ⚠️ PIÈGE (registre) : `setSearchParams(fn)` de React Router ne MET PAS EN
 * FILE les mises à jour comme `useState` — deux appels dans le même tic
 * partent tous deux de l'URL du dernier rendu, et le second efface le
 * premier. `set()` part donc de la DERNIÈRE URL qu'il a lui-même calculée
 * (`pendingRef`) tant que le routeur n'a pas rendu la suivante.
 *
 * Le retour depuis une fiche (`useBackToList`) et la position de défilement
 * (`useListScrollRestoration`) complètent le dispositif plus bas.
 */

/** Un paramètre d'URL typé : sa valeur par défaut, sa lecture, son écriture. */
export interface ListParam<T> {
  defaultValue: T
  /** Lecture tolérante : `null` (absent) ou invalide ⇒ `defaultValue`. */
  parse: (raw: string | null) => T
  serialize: (value: T) => string
}

/** Fabriques des paramètres usuels. */
export const listParam = {
  /** Chaîne libre (recherche, ville…). Défaut omis de l'URL. */
  string(defaultValue = ''): ListParam<string> {
    return {
      defaultValue,
      parse: (raw) => (raw === null ? defaultValue : raw),
      serialize: (value) => value,
    }
  },
  /** Valeur parmi une liste fermée ; toute autre valeur ⇒ défaut. */
  oneOf<T extends string>(values: readonly T[], defaultValue: T): ListParam<T> {
    return {
      defaultValue,
      parse: (raw) => (raw !== null && (values as readonly string[]).includes(raw) ? (raw as T) : defaultValue),
      serialize: (value) => value,
    }
  },
  /** Chaîne non vide quelconque (identifiant de campagne…), défaut sinon. */
  id(defaultValue: string): ListParam<string> {
    return {
      defaultValue,
      parse: (raw) => (raw !== null && raw.trim() !== '' ? raw : defaultValue),
      serialize: (value) => value,
    }
  },
  /** Date saisie `YYYY-MM-DD` (bornes personnalisées) ; autre chose ⇒ défaut. */
  isoDate(defaultValue = ''): ListParam<string> {
    return {
      defaultValue,
      parse: (raw) => (raw !== null && /^\d{4}-\d{2}-\d{2}$/.test(raw) ? raw : defaultValue),
      serialize: (value) => value,
    }
  },
  /** Entier borné (page…). Non numérique ou hors bornes ⇒ défaut. */
  int(defaultValue: number, bounds: { min?: number; max?: number } = {}): ListParam<number> {
    const { min = Number.MIN_SAFE_INTEGER, max = Number.MAX_SAFE_INTEGER } = bounds
    return {
      defaultValue,
      parse: (raw) => {
        if (raw === null || !/^-?\d+$/.test(raw.trim())) return defaultValue
        const value = Number.parseInt(raw, 10)
        return value >= min && value <= max ? value : defaultValue
      },
      serialize: (value) => String(value),
    }
  },
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ListSchema = Record<string, ListParam<any>>
export type ListValues<S extends ListSchema> = { [K in keyof S]: S[K]['defaultValue'] }

/** Lit un schéma depuis des paramètres d'URL. Exporté pour les tests et les lectures hors composant. */
export function readListState<S extends ListSchema>(schema: S, params: URLSearchParams): ListValues<S> {
  const values = {} as ListValues<S>
  for (const key of Object.keys(schema) as Array<keyof S>) {
    values[key] = schema[key].parse(params.get(key as string))
  }
  return values
}

/** Écrit un patch dans une copie des paramètres ; défauts et vides omis. */
export function writeListState<S extends ListSchema>(
  schema: S,
  params: URLSearchParams,
  patch: Partial<ListValues<S>>,
): URLSearchParams {
  const next = new URLSearchParams(params)
  for (const key of Object.keys(patch) as Array<keyof S & string>) {
    const spec = schema[key]
    if (!spec) continue
    const serialized = spec.serialize(patch[key])
    if (serialized === '' || serialized === spec.serialize(spec.defaultValue)) next.delete(key)
    else next.set(key, serialized)
  }
  return next
}

/**
 * État de liste porté par l'URL. Le `schema` doit être une constante de
 * module (référence stable) : ses clés SONT les noms des paramètres d'URL.
 */
export function useListState<S extends ListSchema>(schema: S) {
  const [params, setParams] = useSearchParams()
  const location = useLocation()
  const values = useMemo(() => readListState(schema, params), [schema, params])

  // Dernière URL calculée par `set()` — voir le piège en tête de fichier.
  const pendingRef = useRef(params)
  const renderedRef = useRef(params)
  if (renderedRef.current !== params) {
    renderedRef.current = params
    pendingRef.current = params
  }
  // L'état de navigation (ex. `from`) survit aux réécritures de l'URL.
  const stateRef = useRef(location.state)
  stateRef.current = location.state
  const keyRef = useRef(location.key)
  keyRef.current = location.key

  /**
   * `options.push` (KIT-SECTIONS) : EMPILE une entrée d'historique au lieu de
   * remplacer — ouverture d'une section en portrait, que le geste « retour »
   * doit défaire. `options.state` s'ajoute à l'état porté (marque de l'entrée).
   */
  const set = useCallback(
    (patch: Partial<ListValues<S>>, options: { push?: boolean; state?: Record<string, unknown> } = {}) => {
      const next = writeListState(schema, pendingRef.current, patch)
      if (next.toString() === pendingRef.current.toString()) return
      pendingRef.current = next
      const carried = carryDirectEntry(stateRef.current, keyRef.current)
      const state = options.state
        ? { ...(carried && typeof carried === 'object' ? (carried as Record<string, unknown>) : {}), ...options.state }
        : carried
      setParams(next, { replace: !options.push, state })
    },
    [schema, setParams],
  )

  const reset = useCallback(() => {
    const defaults = {} as Partial<ListValues<S>>
    for (const key of Object.keys(schema) as Array<keyof S>) defaults[key] = schema[key].defaultValue
    set(defaults)
  }, [schema, set])

  return { values, set, reset }
}

// ═══════════════════════════════════════════════════════════════════════════
// RETOUR VERS LA LISTE
// ═══════════════════════════════════════════════════════════════════════════

/**
 * ⚠️ PIÈGE : `replace` sur l'entrée d'ARRIVÉE (lien direct, PWA rouverte —
 * clé `'default'`) lui donne une clé ALÉATOIRE. `useBackToList`, qui lit
 * « historique interne ⇔ clé ≠ default », croyait alors pouvoir remonter
 * l'historique et « Retour » sortait de l'application (onglet du détail
 * campagne écrit dans l'URL, #259). L'entrée d'arrivée garde donc la trace de
 * ce qu'elle est dans son état de navigation, à travers les réécritures.
 */
export const DIRECT_ENTRY_STATE_KEY = 'listDirectEntry'

function isDirectEntry(state: unknown, key: string): boolean {
  return key === 'default' || (state as Record<string, unknown> | null)?.[DIRECT_ENTRY_STATE_KEY] === true
}

function carryDirectEntry(state: unknown, key: string): unknown {
  if (!isDirectEntry(state, key)) return state
  const base = state && typeof state === 'object' ? (state as Record<string, unknown>) : {}
  return { ...base, [DIRECT_ENTRY_STATE_KEY]: true }
}

/** État de navigation posé par une liste quand elle ouvre une fiche. */
export interface ListReturnState {
  /** URL complète de la liste (chemin + paramètres) à retrouver au retour. */
  from: string
}

/**
 * Seuls les chemins INTERNES sont acceptés comme retour : `/…` mais pas
 * `//hôte` (URL de protocole relatif) — un état d'historique forgé ne doit
 * pas pouvoir faire sortir de l'application.
 */
export function safeReturnPath(value: unknown): string | null {
  if (typeof value !== 'string') return null
  if (!value.startsWith('/') || value.startsWith('//') || value.startsWith('/\\')) return null
  return value
}

/** À appeler dans une LISTE : l'état à passer à `navigate(fiche, { state })`. */
export function useListReturnState(): ListReturnState {
  const location = useLocation()
  return { from: location.pathname + location.search }
}

/**
 * Variante hors hook, lue AU CLIC sur `window.location` (que `BrowserRouter`
 * tient synchrone) : pour les composants profonds qui ouvrent une fiche
 * (table du journal, panneau « Numéro inconnu ») sans avoir à s'abonner à
 * la route — leurs specs doublent `react-router-dom` au seul `useNavigate`.
 */
export function listReturnStateFromWindow(): ListReturnState {
  return { from: window.location.pathname + window.location.search }
}

/**
 * Bouton « retour » d'une FICHE. Ordre de résolution :
 *   1. `location.state.from` posé par la liste : si l'entrée précédente de
 *      l'historique est dans l'application (`location.key !== 'default'`),
 *      `navigate(-1)` — l'entrée précédente EST cette liste, avec ses
 *      paramètres, et l'historique ne s'allonge pas ; sinon (fiche rouverte
 *      après un rechargement sans historique), `navigate(from)` ;
 *   2. pas de `from` mais un historique interne : `navigate(-1)` ;
 *   3. ni l'un ni l'autre (lien direct, PWA rouverte) : `defaultPath`.
 */
export function useBackToList(defaultPath: string) {
  const location = useLocation()
  const navigate = useNavigate()
  const from = safeReturnPath((location.state as Partial<ListReturnState> | null)?.from)
  const hasInAppHistory = !isDirectEntry(location.state, location.key)

  const goBack = useCallback(() => {
    if (hasInAppHistory) navigate(-1)
    else navigate(from ?? defaultPath, { state: { restoreScroll: true } })
  }, [defaultPath, from, hasInAppHistory, navigate])

  return {
    /** Liste d'origine connue (`state.from`), sinon `null`. */
    from,
    /** Vrai si le retour remontera l'historique (destination non garantie sans `from`). */
    hasInAppHistory,
    /** Destination quand on ne remonte pas l'historique. */
    target: from ?? defaultPath,
    goBack,
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// POSITION DE DÉFILEMENT
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Le seul conteneur qui défile est le `<main>` du châssis (`AppShell`), qui
 * survit aux changements de route. Il porte `data-scroll-root`.
 */
export const SCROLL_ROOT_SELECTOR = '[data-scroll-root]'
const SCROLL_KEY_PREFIX = 'list-scroll:'

function scrollRoot(): HTMLElement | null {
  return typeof document === 'undefined' ? null : document.querySelector<HTMLElement>(SCROLL_ROOT_SELECTOR)
}

function readScroll(key: string): number | null {
  try {
    const raw = window.sessionStorage.getItem(SCROLL_KEY_PREFIX + key)
    const value = raw === null ? NaN : Number(raw)
    return Number.isFinite(value) && value > 0 ? value : null
  } catch {
    return null
  }
}

function writeScroll(key: string, top: number) {
  try {
    if (top > 0) window.sessionStorage.setItem(SCROLL_KEY_PREFIX + key, String(Math.round(top)))
    else window.sessionStorage.removeItem(SCROLL_KEY_PREFIX + key)
  } catch {
    /* stockage indisponible : la position ne survit pas, la liste marche. */
  }
}

/**
 * Mémorise la position de défilement d'une liste (par URL complète, dans
 * `sessionStorage`) et la RESTAURE au retour, une fois les données rendues
 * (`ready`). Restauration seulement sur un RETOUR (`POP` : précédent,
 * `navigate(-1)`, ou `state.restoreScroll` posé par `useBackToList`) ; une
 * arrivée « en avant » (menu, lien) part du haut — l'`AppShell` remet le
 * `<main>` à zéro à chaque changement de chemin.
 *
 * Limite : une liste à chargement progressif ne retrouve que sa PREMIÈRE
 * page ; la position est bornée par la hauteur rendue.
 */
export function useListScrollRestoration(ready: boolean) {
  const location = useLocation()
  const navigationType = useNavigationType()
  const key = location.pathname + location.search
  const keyRef = useRef(key)
  keyRef.current = key
  const lastTop = useRef(0)

  // Écriture AU FIL DU DÉFILEMENT (une fois par image) — et non au seul
  // démontage. Sous StrictMode (banc, `pnpm dev`), React simule un
  // démontage/remontage : une écriture inconditionnelle au nettoyage y
  // posait 0 et effaçait la position sauvegardée (retour toujours en haut).
  // Même risque à tout remontage (Suspense, HMR) ou si un parent remettait le
  // défilement à zéro avant le nettoyage. Règle : on n'écrit QUE ce qu'un
  // événement `scroll` a réellement observé depuis le montage.
  // `useLayoutEffect` : le nettoyage passe AVANT que la route suivante ne
  // remplace le contenu (le `scrollTop` serait déjà rogné ensuite).
  useLayoutEffect(() => {
    const root = scrollRoot()
    if (!root) return
    // 0 et non `root.scrollTop` : au montage, l'`AppShell` n'a pas encore
    // remis à zéro le défilement de la page PRÉCÉDENTE (les effets enfants
    // passent avant ceux du parent).
    lastTop.current = 0
    let scrolled = false
    let frame: number | null = null
    const raf =
      typeof window.requestAnimationFrame === 'function'
        ? (cb: () => void) => window.requestAnimationFrame(cb)
        : (cb: () => void) => window.setTimeout(cb, 16)
    const caf =
      typeof window.cancelAnimationFrame === 'function'
        ? (id: number) => window.cancelAnimationFrame(id)
        : (id: number) => window.clearTimeout(id)
    const flush = () => {
      frame = null
      writeScroll(keyRef.current, lastTop.current)
    }
    const onScroll = () => {
      scrolled = true
      lastTop.current = root.scrollTop
      if (frame === null) frame = raf(flush)
    }
    root.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      root.removeEventListener('scroll', onScroll)
      if (frame !== null) caf(frame)
      // Aucun défilement observé (remontage StrictMode, montage éclair) : on
      // ne touche à RIEN — la position sauvegardée reste celle d'avant.
      if (scrolled) writeScroll(keyRef.current, lastTop.current)
    }
  }, [])

  const returning =
    navigationType === 'POP' || (location.state as { restoreScroll?: boolean } | null)?.restoreScroll === true
  const restored = useRef(false)
  useEffect(() => {
    if (!ready || restored.current) return
    restored.current = true
    if (!returning) return
    const top = readScroll(key)
    const root = scrollRoot()
    if (top !== null && root) {
      root.scrollTop = top
      lastTop.current = top
    }
    // Une seule tentative par montage : la clé initiale fait foi.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready])
}

// ═══════════════════════════════════════════════════════════════════════════
// CHOIX D'ONGLET (hors URL)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Un choix de filtre retenu pour l'ONGLET (`sessionStorage`), quand l'URL
 * n'est pas le bon support : surface montée dans une page qui réécrit son
 * adresse (la scène de téléphonie, gelée, efface ses paramètres via
 * `useRecallPrefill`) ou composant sans routeur dans ses specs. Lecture
 * tolérante (valeur inconnue ⇒ défaut), écriture silencieuse si le stockage
 * est indisponible.
 */
export function useSessionChoice<T extends string>(
  storageKey: string,
  allowed: readonly T[],
  fallback: T,
): [T, (value: T) => void] {
  const [value, setValue] = useState<T>(() => {
    try {
      const stored = window.sessionStorage.getItem(storageKey)
      return stored !== null && (allowed as readonly string[]).includes(stored) ? (stored as T) : fallback
    } catch {
      return fallback
    }
  })
  const choose = useCallback(
    (next: T) => {
      setValue(next)
      try {
        if (next === fallback) window.sessionStorage.removeItem(storageKey)
        else window.sessionStorage.setItem(storageKey, next)
      } catch {
        /* stockage indisponible : le choix ne survit pas, le filtre marche. */
      }
    },
    [fallback, storageKey],
  )
  return [value, choose]
}
